/**
 * 🎓 RÉSUMÉ PÉDAGOGIQUE - COMPOSANT DASHBOARD CENTRALISÉ
 * ======================================================
 * 
 * UNIQUE source de vérité pour TOUTES les données de progression.
 * Affiche le résumé de la progression pédagogique dans le UserDashboard.
 * 
 * Données centralisées:
 * - Progression globale Owen
 * - Objectifs validés / restants
 * - Modules terminés / en cours
 * - Preuves collectées
 * - Diagnostics d'entrée/sortie
 * - Attestation de parcours (UNIQUE)
 * 
 * 🔒 P0 FIX: Bloc "Dernière activité" supprimé
 * 🔒 P0 FIX FINAL (Jan 2026): Utilise useGlobalProgress hook partagé
 * 
 * Lecture seule - Owen est l'unique autorité.
 * Qualiopi-ready
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  Target, Award, Brain, TrendingUp, CheckCircle2, 
  FileText, Shield, Sparkles, ChevronRight, Lock,
  BookOpen, Activity, Calendar
  // 🔒 P0 FIX: Clock supprimé (bloc "Dernière activité" retiré)
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { getBackendUrl } from '../config/environment';
import { subscribeToEvaluation, subscribeToRefresh } from '../utils/pedagogicalEvents';
import { useGlobalProgress } from '../hooks/useGlobalProgress';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * Composant Résumé Pédagogique - CENTRALISÉ
 * Toutes les données de progression sont ici.
 */
const PedagogicalSummary = () => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(null);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [multiModuleEligibility, setMultiModuleEligibility] = useState(null);
  const [globalCertEligibility, setGlobalCertEligibility] = useState(null);
  const [globalCertBusy, setGlobalCertBusy] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // 🔒 P0 FIX FINAL (Jan 2026): Utiliser le hook partagé pour la progression globale
  // Même source que TrainingDashboard_Premium.js = zéro désynchronisation
  const { 
    globalProgress, 
    validatedModules, 
    totalModules,
    validatedObjectives,
    totalObjectives,
    modulesInProgress,
    totalEvidences,
    refresh: refreshGlobalProgress
  } = useGlobalProgress();

  // 🔄 Fonction de chargement des données (memoized)
  // 🔒 P0-J FIX: Cache-bust pour garantir données fraîches
  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      
      const timestamp = Date.now();
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const headers = token ? { 
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      } : {};
      
      const [summaryRes, diagnosticRes, activityRes, multiModuleRes, globalCertRes] = await Promise.all([
        axios.get(`${API}/pedagogical/dashboard/summary?_t=${timestamp}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/diagnostic/my-diagnostics?_t=${timestamp}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/me?_t=${timestamp}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/certificate/multi-module/eligibility?_t=${timestamp}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/me/global-certificate/eligibility?_t=${timestamp}`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setSummaryData(summaryRes.data);
      setDiagnosticData(diagnosticRes.data);
      setActivityData(activityRes.data);
      setMultiModuleEligibility(multiModuleRes.data);
      setGlobalCertEligibility(globalCertRes.data);
      
      console.log(`[PedagogicalSummary] ✅ [${timestamp}] Données rechargées`);
    } catch (err) {
      console.error('Erreur chargement données pédagogiques:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🎯 Chargement initial
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // 🔔 Écouter les événements de refresh (évaluation Owen terminée)
  useEffect(() => {
    console.log('[PedagogicalSummary] 👂 Abonnement aux événements pédagogiques');
    
    const unsubscribeEval = subscribeToEvaluation((data) => {
      console.log('[PedagogicalSummary] 📡 Événement evaluation_complete reçu');
      fetchData(false);
    });

    const unsubscribeRefresh = subscribeToRefresh((data) => {
      console.log('[PedagogicalSummary] 📡 Événement refresh reçu');
      fetchData(false);
    });

    return () => {
      unsubscribeEval();
      unsubscribeRefresh();
    };
  }, [fetchData]);

  // 🎓 Téléchargement attestation multi-modules CPF (UNIQUE)
  const handleDownloadMultiModuleCertificate = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await axios.get(`${API}/certificate/multi-module/generate`, {
        responseType: 'blob',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'attestation_parcours_coachdia.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Erreur téléchargement attestation parcours:', err);
    }
  };

  // 🎓 Iter387 — Génération + téléchargement du Certificat de Formation Complète
  const handleDownloadGlobalCertificate = async () => {
    if (globalCertBusy) return;
    setGlobalCertBusy(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      // Idempotent : crée si absent, retourne sinon
      await axios.post(`${API}/me/global-certificate/generate`, null, { headers });
      const response = await axios.get(`${API}/me/global-certificate/download`, {
        responseType: 'blob',
        headers,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certificat_coachdia.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // Recharge l'éligibilité (pour récupérer certificate doc / revoked status)
      fetchData(false);
    } catch (err) {
      console.error('Erreur téléchargement certificat global:', err);
    } finally {
      setGlobalCertBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-purple-200/50 bg-white/80 backdrop-blur-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-3">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full"
            />
            <span className="text-gray-600">Chargement...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // 🔒 P0 FIX FINAL (Jan 2026): Les données de progression viennent du hook useGlobalProgress()
  // Plus de déclarations locales qui pourraient causer une désynchronisation
  // Les variables globalProgress, validatedModules, etc. sont définies par le hook en haut du composant

  return (
    <div className="space-y-6">
      {/* ====================================== */}
      {/* HEADER SECTION - Progression Globale */}
      {/* ====================================== */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="border-0 bg-gradient-to-br from-[#3B1E73] to-[#8B5CF6] text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Brain className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Gouvernance Pédagogique Owen</h3>
                  <p className="text-white/80 text-sm mt-1">
                    Votre progression est évaluée exclusivement par Owen
                  </p>
                </div>
              </div>
              
              {/* Circular Progress - UNIQUE affichage de la progression % */}
              <div className="flex items-center gap-6">
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="w-24 h-24"
                >
                  <CircularProgressbar
                    value={globalProgress}
                    text={`${globalProgress}%`}
                    styles={buildStyles({
                      textSize: '22px',
                      textColor: "#ffffff",
                      pathColor: "#f0abfc",
                      trailColor: "rgba(255,255,255,0.2)",
                      pathTransitionDuration: 0.5,
                    })}
                  />
                </motion.div>
                <div className="text-right">
                  <div className="text-white/80 text-sm">Progression globale</div>
                  <div className="text-lg font-semibold mt-1">Certifiée Owen</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* ====================================== */}
      {/* STATS GRID - KPIs Centralisés */}
      {/* ====================================== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Objectifs Validés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border border-purple-200/50 bg-white/80 backdrop-blur-sm h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-purple-100 rounded-lg">
                  <Target className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-xs font-medium text-gray-600">Objectifs Validés</span>
              </div>
              <div className="text-2xl font-bold text-[#2B1854]">
                {validatedObjectives}
                <span className="text-sm text-gray-400">/{totalObjectives}</span>
              </div>
              <Progress 
                value={(validatedObjectives / totalObjectives) * 100} 
                className="mt-2 h-1.5" 
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Modules Terminés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="border border-purple-200/50 bg-white/80 backdrop-blur-sm h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <BookOpen className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-xs font-medium text-gray-600">Modules Terminés</span>
              </div>
              <div className="text-2xl font-bold text-[#2B1854]">
                {validatedModules}
                <span className="text-sm text-gray-400">/{totalModules}</span>
              </div>
              <Progress 
                value={(validatedModules / totalModules) * 100} 
                className="mt-2 h-1.5" 
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Modules En Cours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border border-purple-200/50 bg-white/80 backdrop-blur-sm h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-pink-100 rounded-lg">
                  <Activity className="w-4 h-4 text-pink-600" />
                </div>
                <span className="text-xs font-medium text-gray-600">En Cours</span>
              </div>
              <div className="text-2xl font-bold text-[#2B1854]">
                {modulesInProgress}
              </div>
              <p className="text-xs text-gray-500 mt-1">modules actifs</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Preuves Collectées */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="border border-purple-200/50 bg-white/80 backdrop-blur-sm h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-amber-100 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                </div>
                <span className="text-xs font-medium text-gray-600">Preuves</span>
              </div>
              <div className="text-2xl font-bold text-[#2B1854]">
                {summaryData?.total_evidences || 0}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                <Lock className="w-3 h-3 inline mr-1" />
                par Owen
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* ====================================== */}
      {/* 🔒 P0 FIX: BLOC "DERNIÈRE ACTIVITÉ" SUPPRIMÉ */}
      {/* Supprimé conformément aux spécifications P0 */}
      {/* ====================================== */}

      {/* ====================================== */}
      {/* DIAGNOSTIC SECTION */}
      {/* ====================================== */}
      {diagnosticData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="border border-purple-200/50 bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Brain className="w-5 h-5 text-purple-600" />
                Diagnostic Pédagogique
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Diagnostic Entrée */}
                <div className={`p-4 rounded-xl border ${
                  diagnosticData.has_entry 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Diagnostic d&apos;entrée</span>
                    {diagnosticData.has_entry ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Complété
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                    )}
                  </div>
                  {diagnosticData.entry_diagnostic && (
                    <div className="text-2xl font-bold text-[#2B1854]">
                      {diagnosticData.entry_diagnostic.percentage}%
                      <span className="text-sm font-normal text-gray-500 ml-2">score initial</span>
                    </div>
                  )}
                </div>

                {/* Diagnostic Sortie — Progressif */}
                <div className={`p-4 rounded-xl border ${
                  diagnosticData.has_exit 
                    ? 'bg-green-50 border-green-200' 
                    : diagnosticData.progressive_exit?.has_data
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Diagnostic de sortie</span>
                    {diagnosticData.has_exit ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Généré
                      </Badge>
                    ) : diagnosticData.progressive_exit?.has_data ? (
                      <Badge className="bg-blue-100 text-blue-700">
                        En progression
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600">Non disponible</Badge>
                    )}
                  </div>
                  {diagnosticData.has_exit && diagnosticData.exit_diagnostic ? (
                    <div>
                      <div className="text-2xl font-bold text-[#2B1854]">
                        {diagnosticData.exit_diagnostic.final_score_estimate}%
                        <span className="text-green-600 text-sm font-semibold ml-2">
                          (+{diagnosticData.exit_diagnostic.delta_progression}%)
                        </span>
                      </div>
                    </div>
                  ) : diagnosticData.progressive_exit?.has_data ? (
                    <div>
                      <div className="text-2xl font-bold text-[#2B1854]">
                        {diagnosticData.progressive_exit.score}%
                        {diagnosticData.progressive_exit.delta !== null && diagnosticData.progressive_exit.delta !== 0 && (
                          <span className={`text-sm font-semibold ml-2 ${diagnosticData.progressive_exit.delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            ({diagnosticData.progressive_exit.delta > 0 ? '+' : ''}{diagnosticData.progressive_exit.delta}%)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        {diagnosticData.progressive_exit.validated_objectives}/{diagnosticData.progressive_exit.total_objectives} objectifs validés
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Disponible après validation d&apos;objectifs
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ====================================== */}
      {/* ATTESTATION MULTI-MODULES CPF (UNIQUE) */}
      {/* ====================================== */}
      {multiModuleEligibility?.is_eligible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="border border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-emerald-200">
                    <Award className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2B1854]">
                      Attestation de Parcours Multi-Modules
                    </h4>
                    <p className="text-sm text-emerald-700">
                      {multiModuleEligibility.total_modules_validated} modules validés — Conforme CPF
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleDownloadMultiModuleCertificate}
                  className="w-full gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  data-testid="download-multi-module-certificate-btn"
                >
                  <Award className="w-4 h-4" />
                  Télécharger Attestation Parcours
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ====================================== */}
      {/* CERTIFICAT DE FORMATION COMPLÈTE (Iter387) */}
      {/* ====================================== */}
      {(globalCertEligibility?.is_eligible || globalCertEligibility?.certificate) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          data-testid="global-certificate-card"
        >
          <Card className={
            globalCertEligibility?.certificate?.revoked
              ? "border border-rose-300 bg-gradient-to-br from-rose-50 to-pink-50 overflow-hidden"
              : "border-2 border-[#C9A962] bg-gradient-to-br from-[#FBF9F3] to-[#F5EDD7] overflow-hidden shadow-md"
          }>
            <CardContent className="p-6">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <div className={
                    globalCertEligibility?.certificate?.revoked
                      ? "p-3 rounded-xl bg-rose-200"
                      : "p-3 rounded-xl bg-[#EDD9A0]"
                  }>
                    <Award className={
                      globalCertEligibility?.certificate?.revoked
                        ? "w-6 h-6 text-rose-700"
                        : "w-6 h-6 text-[#92710A]"
                    } />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }} data-testid="global-certificate-title">
                      Certificat de Formation Complète
                    </h4>
                    {globalCertEligibility?.certificate?.revoked ? (
                      <p className="text-sm text-rose-700" data-testid="global-certificate-revoked-label">
                        Certificat révoqué le {globalCertEligibility.certificate.revoked_at ? new Date(globalCertEligibility.certificate.revoked_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </p>
                    ) : (
                      <p className="text-sm text-[#92710A]">
                        5/5 piliers validés · Document officiel signé SHA-256
                      </p>
                    )}
                    {globalCertEligibility?.certificate?.display_number && (
                      <p className="text-[11px] font-mono font-semibold text-[#92710A] mt-1" data-testid="global-certificate-display-number">
                        N° {globalCertEligibility.certificate.display_number}
                      </p>
                    )}
                  </div>
                </div>

                {globalCertEligibility?.certificate?.revoked ? (
                  <div className="text-xs text-rose-800 italic" data-testid="global-certificate-revoked-reason">
                    Motif : {globalCertEligibility.certificate.revoked_reason || 'Non précisé'}
                  </div>
                ) : (
                  <Button
                    onClick={handleDownloadGlobalCertificate}
                    disabled={globalCertBusy}
                    className="w-full gap-2 text-white font-semibold"
                    style={{ background: 'linear-gradient(135deg, #C9A962 0%, #92710A 100%)' }}
                    data-testid="download-global-certificate-btn"
                  >
                    <Award className="w-4 h-4" />
                    {globalCertBusy ? 'Préparation…' : 'Télécharger mon certificat'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ====================================== */}
      {/* QUALIOPI FOOTER */}
      {/* ====================================== */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
        <Shield className="w-3.5 h-3.5" />
        <span>Conforme aux indicateurs Qualiopi - Évaluation exclusive par Owen</span>
      </div>
    </div>
  );
};

export default PedagogicalSummary;
